import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { replaceLogoReferencesWithActualLogo, preserveManuallyUploadedImages, extractImageMap, restoreImagesFromMap } from './PDFDesignerHelpers';

const useOttoPDFDesigner = ({ drawOptions, logo, fileMap, updateDrawOptions, setItems, setRealItems, decorateDrawOptionsWithFiles, currentItems }) => {
  const [ottoChatVisible, setOttoChatVisible] = useState(false);
  const [initialDrawOptions, setInitialDrawOptions] = useState(null);
  const [initialImageMap, setInitialImageMap] = useState(null);
  const [historyStack, setHistoryStack] = useState([]);

  const clientPortalSettings = useSelector((state) => state.clientPortal.settings);
  const { settings } = useSelector((state) => state.settings.company);

  useEffect(() => {
    if (initialDrawOptions === null && drawOptions?.length > 0) {
      setInitialDrawOptions(JSON.parse(JSON.stringify(drawOptions)));
      setInitialImageMap(extractImageMap(currentItems));
    }
  }, [drawOptions, initialDrawOptions, currentItems]);

  const handleOttoChatToggle = useCallback(() => {
    setOttoChatVisible((prev) => !prev);
  }, []);

  const handleOttoDrawOptionsUpdate = useCallback((newDrawOptions) => {
    if (drawOptions && drawOptions.length > 0) {
      const imageMap = extractImageMap(currentItems);
      setHistoryStack((prev) => [...prev, {
        drawOptions: JSON.parse(JSON.stringify(drawOptions)),
        imageMap,
      }]);
    }

    const drawOptionsWithImages = preserveManuallyUploadedImages(newDrawOptions, currentItems);
    const drawOptionsWithActualLogo = replaceLogoReferencesWithActualLogo(drawOptionsWithImages, logo);
    updateDrawOptions(drawOptionsWithActualLogo);
    setItems(decorateDrawOptionsWithFiles({ drawOptions: drawOptionsWithActualLogo, fileMap }));
  }, [drawOptions, logo, fileMap, updateDrawOptions, setItems, decorateDrawOptionsWithFiles, currentItems]);

  const applyDrawOptions = useCallback((restoredDrawOptions) => {
    const restoredDrawOptionsWithLogo = replaceLogoReferencesWithActualLogo(restoredDrawOptions, logo);
    updateDrawOptions(restoredDrawOptionsWithLogo);
    const decoratedItems = decorateDrawOptionsWithFiles({ drawOptions: restoredDrawOptionsWithLogo, fileMap });
    setItems(decoratedItems);
    setRealItems(decoratedItems);
  }, [logo, fileMap, updateDrawOptions, setItems, setRealItems, decorateDrawOptionsWithFiles]);

  const handleUndo = useCallback(() => {
    if (historyStack.length > 0) {
      const previousState = historyStack[historyStack.length - 1];
      const newHistoryStack = historyStack.slice(0, -1);

      const restoredDrawOptions = restoreImagesFromMap(previousState.drawOptions, previousState.imageMap);
      applyDrawOptions(restoredDrawOptions);
      setHistoryStack(newHistoryStack);

      return true;
    }
    return false;
  }, [historyStack, applyDrawOptions]);

  const handleRestoreToInitial = useCallback(() => {
    if (initialDrawOptions && initialImageMap) {
      const restoredDrawOptions = restoreImagesFromMap(initialDrawOptions, initialImageMap);
      applyDrawOptions(restoredDrawOptions);
    }
  }, [initialDrawOptions, initialImageMap, applyDrawOptions]);

  const companySettings = {
    companyName: settings?.name || 'Unknown Company',
    settings,
    brandColors: {
      primary: clientPortalSettings?.primaryColor,
      accent: clientPortalSettings?.accentColor,
    },
  };

  return {
    ottoChatVisible,
    handleOttoChatToggle,
    handleOttoDrawOptionsUpdate,
    handleRestoreToInitial,
    handleUndo,
    canUndo: historyStack.length > 0,
    companySettings,
  };
};

export default useOttoPDFDesigner;
